import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { classNames, isIndianAddressWithForeignPhone, redirectUrl } from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";
import React, { useState, useMemo } from "react";
import { CaretRight, Keyboard } from "@phosphor-icons/react";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { getRequest } from "lib/core/apiClient";
import { getUnApplicablePaymentReason, parseMethodUiAttributes } from "lib/utils/payments";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLocale } from "lib/hooks/useLocale";
import { useAddressValidation } from "lib/hooks/useAddressValidation";
import { useCheckoutErrors } from "lib/hooks/useCheckoutErrors";
import { useUserContext } from "lib/contexts/UserProvider";
import SuggestedMethodWrapper from "components/common/SuggestedMethodWrapper";
import LogosStack from "components/common/LogosStack";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { PaymentMode } from "lib/types/payments";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";

const ShopifyGiftCard: React.FC = ({}) => {
  const { t } = useLocale();
  const {
    state: {
      checkoutId,
      hasDefaultShippingHandleSelected,
      checkoutValidations,
      shippingHandles,
      activeComponent,
    },
    actions: { setActiveComponent },
  } = useCheckoutContext();

  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const { initialCheckoutStep } = checkoutMetadata ?? {};

  const { verifyDefaultAddress } = useAddressValidation();
  const { triggerCheckoutErrorInfoToast } = useCheckoutErrors();
  const { openPopup } = usePopupOrchestrator();
  const {
    state: { user },
  } = useUserContext();

  const {
    state: { paymentMethods },
  } = usePaymentContext();

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const gcModeConfig = paymentMethods?.find((e: PaymentMode) => e.name === "SHOPIFY_GC");
  const unapplicabilityReason = getUnApplicablePaymentReason(
    gcModeConfig?.unavailabilityReasons || [],
    gcModeConfig?.metadata?.title || "",
  );

  const methodUiAttributes = useMemo(() => {
    return parseMethodUiAttributes({mode: gcModeConfig, checkoutMetadata});
  }, [gcModeConfig, checkoutMetadata]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleGCClick = async () => {
    const isAddressValid = verifyDefaultAddress();

    if (initialCheckoutStep === "PAYMENTS") {
      if (!Boolean(user?.addresses?.length)) {
        openPopup("ADDRESS_NEW", {});
        return;
      }

      if (!isAddressValid) {
        if (!Boolean(checkoutValidations?.address?.metadata)) {
          openPopup("ADDRESS_LIST", {});
          errorToast(
            isIndianAddressWithForeignPhone(user?.default_shipping_address)
              ? t("indian_phone_required_for_indian_address")
              : t("serviceability_error"),
          );
          return;
        }
        openPopup("ADDRESS_EDIT", { addressId: checkoutValidations?.address?.metadata?.addressId ?? "" });
        return;
      }

      if (!Boolean(shippingHandles?.length)) {
        triggerCheckoutErrorInfoToast(t("serviceability_error"));
        setActiveComponent("SHIPPING_ADDRESS_SECTION");
        return;
      }
    }

    if (!Boolean(hasDefaultShippingHandleSelected)) {
      openPopup("SHIPPING_HANDLES", {});
      return;
    }

    try {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
        eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
        eventType: "click",
        metaData: {
          methodType: "SHOPIFY_GC",
        },
      });
      setIsLoading(true);
      const response = await getRequest(`/checkout/v1/checkout/${checkoutId}/draft-order`);
      const success_url = response?.metadata?.action_urls?.GIFT_CARD_TILE?.success_url;
      if (success_url) {
        redirectUrl(success_url);
      } else {
        throw new Error("Error while applying gift cards");
      }
    } catch (err) {
      errorToast(t("shopify_gift_card_error"));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderGiftCardMethod = () => {
    return (
      <div
        className={classNames(
          "group flex items-center px-3.5 py-2.5 border-[1.5px] rounded-2xl gap-3 cursor-pointer w-full min-h-14",
          gcModeConfig?.isAvailable ? "hover:shadow-[0_0_0_100px_rgba(113,113,123,0.15)_inset]" : "",
        )}
        style={{
          borderColor: methodUiAttributes.paymentCard.borderColor,
          backgroundColor: methodUiAttributes.paymentCard.backgroundColor,
        }}>
        {methodUiAttributes.layout === "ICONS" && (
          <div className="flex p-1 items-center">
            <Keyboard
              size={28}
              color={gcModeConfig?.isAvailable ? methodUiAttributes.paymentCard.iconColor : "#949494"}
              weight="duotone"
            />
          </div>
        )}

        <div className="flex w-full flex-1 flex-col items-start justify-start space-y-1">
          <div className="flex gap-1 items-center">
            <h3
              className="text-sm font-medium"
              style={{
                color: gcModeConfig?.isAvailable ? methodUiAttributes.paymentCard.textColor : "#949494",
              }}>
              {Boolean(methodUiAttributes.title)
                ? methodUiAttributes.title
                : gcModeConfig?.metadata?.title ?? ""}
            </h3>
            {methodUiAttributes.layout === "LOGOS" && <LogosStack logos={methodUiAttributes.icons} />}
          </div>
          {!Boolean(methodUiAttributes.paymentCard.isSubtitleHidden) && (
            <p
              className="text-left text-xs font-normal"
              style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {Boolean(methodUiAttributes.subtitle)
                ? methodUiAttributes.subtitle
                : gcModeConfig?.metadata?.description ?? ""}
            </p>
          )}
        </div>

        <CaretRight size={14} weight="bold" style={{ color: methodUiAttributes.paymentCard.textColor }} />
      </div>
    );
  };

  return (
    <>
      <SuggestedMethodWrapper
        isSuggested={methodUiAttributes.isSuggested}
        title={methodUiAttributes.suggestedTile?.title}
        backgroundColor={methodUiAttributes.suggestedTile?.backgroundColor}
        textColor={methodUiAttributes.suggestedTile?.textColor}>
        <button className="w-full" disabled={!gcModeConfig?.isAvailable} onClick={handleGCClick}>
          {renderGiftCardMethod()}
          {Boolean(!gcModeConfig?.isAvailable) && Boolean(unapplicabilityReason) && (
            <div className="bg-[#FAFAFA]">
              <p className="px-3 py-2 text-left text-xs font-normal text-gray-dark">
                {unapplicabilityReason}
              </p>
            </div>
          )}
        </button>
      </SuggestedMethodWrapper>
      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default ShopifyGiftCard;
